'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { getCurrentGatewayUrl } from '../../../utils/environmentDetection';
import { getAuthHeaders } from '../../../utils/authHeaders';

type UseCredentialMenuOtpOptions = {
  applicationId?: string;
  credentialId: string;
  mfaConfigId: string;
  enabled?: boolean;
};

export function useCredentialMenuOtp({
  applicationId,
  credentialId,
  mfaConfigId,
  enabled = true,
}: UseCredentialMenuOtpOptions) {
  const [otpCode, setOtpCode] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const isRefreshingRef = useRef(false);
  const expiryRefreshBlockedRef = useRef(false);

  const fetchOtp = useCallback(async (): Promise<boolean> => {
    if (!applicationId || !enabled) {
      return false;
    }

    setIsLoading(true);
    setHasError(false);

    try {
      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.getMfaMessages({
        params: {
          id: applicationId,
          credentialId,
          mfaConfigId,
        },
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: authHeaders,
      });

      if (response.status !== 200 || !response.body.success) {
        setOtpCode('');
        setSecondsLeft(0);
        setHasError(true);

        return false;
      }

      const message = response.body.messages?.[0];
      const code = response.body.otpCode ?? message?.content ?? '';

      setOtpCode(code);
      setSecondsLeft(message?.expiresInSeconds ?? 30);
      setHasError(false);
      expiryRefreshBlockedRef.current = false;

      return true;
    } catch (error) {
      console.error('Failed to fetch OTP code', error);
      setOtpCode('');
      setSecondsLeft(0);
      setHasError(true);

      return false;
    } finally {
      setIsLoading(false);
    }
  }, [applicationId, credentialId, enabled, mfaConfigId]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    void fetchOtp();
  }, [enabled, fetchOtp]);

  useEffect(() => {
    if (!enabled) {
      expiryRefreshBlockedRef.current = false;
      setHasError(false);
      setOtpCode('');
      setSecondsLeft(0);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !otpCode) {
      return;
    }

    const timer = setInterval(() => {
      setSecondsLeft((previousSeconds) => Math.max(0, previousSeconds - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [enabled, otpCode]);

  useEffect(() => {
    if (
      !enabled ||
      !otpCode ||
      secondsLeft > 0 ||
      isRefreshingRef.current ||
      expiryRefreshBlockedRef.current
    ) {
      return;
    }

    isRefreshingRef.current = true;

    void fetchOtp()
      .then((success) => {
        if (!success) {
          expiryRefreshBlockedRef.current = true;
        }
      })
      .finally(() => {
        isRefreshingRef.current = false;
      });
  }, [enabled, fetchOtp, otpCode, secondsLeft]);

  return {
    otpCode,
    secondsLeft,
    isLoading,
    hasError,
  };
}
